import generalIcons from "@planningcenter/icons/sprites/general.svg"
import groupsIcons from "@planningcenter/icons/sprites/groups.svg"
import { Symbol } from "@planningcenter/icons/symbol"
import { Menu, MenuButton, MenuLink, MenuList } from "@reach/menu-button"
import { any, arrayOf, bool, number, object, string } from "prop-types"
import React, { useEffect, useState } from "react"

import {
  ChatDrawer,
  ChatDrawerProvider,
  ChatIframe,
  useChatDrawer,
} from "./Chat"

const HEADER_HEIGHT = 136
const AVATAR_WIDTH = 32
const AVATAR_HEIGHT = AVATAR_WIDTH

Header.propTypes = {
  chatUnread: bool,
  links: arrayOf(object).isRequired,
  loginLink: object.isRequired,
  myChurchCenterLink: object.isRequired,
  notificationCount: number,
  notificationLink: object.isRequired,
  organizationDisplayName: string.isRequired,
  organizationImageUrl: string,
  organizationLink: object.isRequired,
  pcoChatEnabled: bool,
  personDisplayName: string.isRequired,
  personIdentified: bool.isRequired,
  personImageUrl: string.isRequired,
}
export function Header({
  links,
  loginLink,
  myChurchCenterLink,
  notificationCount,
  notificationLink,
  organizationDisplayName,
  organizationImageUrl,
  organizationLink,
  pcoChatEnabled = false,
  personDisplayName,
  personIdentified,
  personImageUrl,
  chatUnread = false,
}) {
  const scrollPosition = useScrollPosition()
  const condenseHeader = scrollPosition > HEADER_HEIGHT

  return (
    <ChatDrawerProvider pcoChatEnabled={pcoChatEnabled}>
      <HeaderStyleSheet />
      {condenseHeader && <div className="condensed-header-placeholder" />}
      <header className={`Header ${condenseHeader && "condensed-header"}`}>
        <div className="Header__layout">
          <div className="Header__org-wrapper">
            <Link {...organizationLink} className="Header__org-link">
              {organizationImageUrl ? (
                <img
                  src={organizationImageUrl}
                  alt={organizationDisplayName}
                  className="Header__org-avatar"
                />
              ) : (
                <h1 className="Header__org-name">{organizationDisplayName}</h1>
              )}
            </Link>
          </div>
          <BiggerNavigation
            links={links}
            loginLink={loginLink}
            myChurchCenterLink={myChurchCenterLink}
            notificationCount={notificationCount}
            notificationLink={notificationLink}
            personDisplayName={personDisplayName}
            personIdentified={personIdentified}
            personImageUrl={personImageUrl}
            chatUnread={chatUnread}
          />
          <SmallerNavigation
            links={links}
            loginLink={loginLink}
            myChurchCenterLink={myChurchCenterLink}
            notificationCount={notificationCount}
            notificationLink={notificationLink}
            personDisplayName={personDisplayName}
            personIdentified={personIdentified}
            personImageUrl={personImageUrl}
            chatUnread={chatUnread}
          />
        </div>
      </header>
      {/* After the header so the drawer follows the nav that opens it in the Tab sequence. */}
      {pcoChatEnabled && (
        <ChatDrawer>
          <ChatIframe />
        </ChatDrawer>
      )}
    </ChatDrawerProvider>
  )
}

function useScrollPosition() {
  const [scrollPosition, setScrollPosition] = useState(0)
  const handleScroll = React.useCallback(
    () => setScrollPosition(window.pageYOffset),
    [setScrollPosition],
  )

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true })

    return () => window.removeEventListener("scroll", handleScroll)
  }, [handleScroll])

  return scrollPosition
}

BiggerNavigation.propTypes = {
  chatUnread: bool,
  links: arrayOf(object).isRequired,
  loginLink: object.isRequired,
  myChurchCenterLink: object.isRequired,
  notificationCount: number,
  notificationLink: object.isRequired,
  personDisplayName: string.isRequired,
  personIdentified: bool.isRequired,
  personImageUrl: string.isRequired,
}
function BiggerNavigation({
  links,
  loginLink,
  myChurchCenterLink,
  notificationCount,
  notificationLink,
  personDisplayName,
  personIdentified,
  personImageUrl,
  chatUnread,
}) {
  let leadingLinks, moreLinks

  if (links.length > 5) {
    leadingLinks = links.slice(0, 4)
    moreLinks = links.slice(4)
  } else {
    leadingLinks = links
  }

  return (
    <nav className="Navigation d-n@iframe">
      {leadingLinks.map(({ key, ...props }) => (
        <Link className="Navigation__link" key={key} {...props} />
      ))}

      {moreLinks && (
        <Menu>
          {({ isOpen }) => (
            <React.Fragment>
              <MenuButton className="Navigation__more-button Navigation__link">
                More
                <span className="Navigation__more-button-toggle">
                  <ChevronIcon
                    style={{
                      transform: isOpen ? "scaleY(-1)" : "scaleY(1)",
                      transition: "transform 0.2s",
                    }}
                  />
                </span>
              </MenuButton>
              <MenuList className="Navigation-menu">
                {moreLinks.map(({ key, ...link }) => (
                  <MenuLink className="Navigation-link" key={key} {...link} />
                ))}
              </MenuList>
            </React.Fragment>
          )}
        </Menu>
      )}

      {personIdentified ? (
        <React.Fragment>
          <NotificationBell
            notificationCount={notificationCount}
            notificationLink={notificationLink}
          />
          <ChatButton unread={chatUnread} />
          <Link className="Navigation__link" {...myChurchCenterLink}>
            <MeAvatar
              displayName={personDisplayName}
              imageUrl={personImageUrl}
            />
          </Link>
        </React.Fragment>
      ) : (
        <Link className="Navigation__link" {...loginLink} />
      )}
    </nav>
  )
}

SmallerNavigation.propTypes = {
  chatUnread: bool,
  links: arrayOf(object).isRequired,
  loginLink: object.isRequired,
  myChurchCenterLink: object.isRequired,
  notificationCount: number,
  notificationLink: object.isRequired,
  personDisplayName: string.isRequired,
  personIdentified: bool.isRequired,
  personImageUrl: string.isRequired,
}
function SmallerNavigation({
  links,
  loginLink,
  myChurchCenterLink,
  notificationCount,
  notificationLink,
  personDisplayName,
  personIdentified,
  personImageUrl,
  chatUnread,
}) {
  return (
    <nav className="AltNavigation d-n@iframe">
      {personIdentified && (
        <React.Fragment>
          <NotificationBell
            notificationCount={notificationCount}
            notificationLink={notificationLink}
          />
          <ChatButton unread={chatUnread} />
          <Link className="Navigation__link" {...myChurchCenterLink}>
            <img
              src={personImageUrl}
              alt={`${personDisplayName}'s avatar`}
              className="AltNavigation__person-avatar"
            />
          </Link>
        </React.Fragment>
      )}
      <Menu>
        {() => (
          <React.Fragment>
            <MobileMenuButton />
            <MenuList className="AltNavigation__layout">
              <div
                className={`AltNavigation__person-box ${
                  personIdentified && "logged-in"
                }`}
              >
                <div className="display-flex align-items-center justify-content-space-between">
                  <div className="AltNavigation__person" />
                  <MobileMenuButton />
                </div>
              </div>
              {links.map(({ key, ...props }) => (
                <MenuLink
                  className="AltNavigation__link"
                  key={key}
                  {...props}
                />
              ))}
              {!personIdentified && (
                <MenuLink className="AltNavigation__link" {...loginLink} />
              )}
            </MenuList>
          </React.Fragment>
        )}
      </Menu>
    </nav>
  )
}

function MobileMenuButton() {
  return (
    <MenuButton className="AltNavigation__trigger">
      <div className="icon">
        <span className="line"></span>
        <span className="line"></span>
        <span className="line"></span>
      </div>
    </MenuButton>
  )
}

const CUTOFF_WIDTH = 720

function HeaderStyleSheet() {
  const headerStyleString = `
  /* Header */
  .Header {
    background-color: var(--color-tint9);
    border-bottom: 1px solid var(--color-tint7);
    height: ${HEADER_HEIGHT}px;
    padding: 1.5rem 2rem 1.5rem 1.5rem;
    z-index: 1000; /* Thanks to Vimeo for setting their z-index to 999 😠*/
  }

  .iframed .Header { position: relative }

  @media (min-width: ${CUTOFF_WIDTH}px) {
    .Header { padding: 1.5rem 2rem }
  }

  .Header__layout {
    align-items: center;
    display: flex;
    height: 100%;
    justify-content: space-between;
    margin: 0 auto;
    max-width: 900px;
    width: 100%;
  }
  .Header__org-wrapper {
    padding-right: 2rem;
  }
  .Header__org-link {
    display: block;
  }
  .Header__org-name {
    font-size: 18px;
    line-height: 1.2;
    margin: 0;
    max-width: 220px;
  }
  .Header__org-avatar {
    max-height: 80px;
    width: 100%;
    max-width: 200px;
    object-fit: contain;
    object-position: center left;
  }
  [data-color-scheme="dark"][data-contrast="low"] .Header__org-avatar {
    background-color: var(--color-tint0);
    border-radius: 4px;
    padding: 2px;
  }

  /* Condensed Header */
  .condensed-header {
    border-bottom-color: var(--color-tint6);
    height: ${HEADER_HEIGHT / 2}px;
    position: fixed;
    top: 0;
    width: 100%;
    animation: fade-in 0.25s;
  }

  .condensed-header-placeholder {
    height: ${HEADER_HEIGHT}px; /* this keeps the browser from jumping when scrolling to the bottom of a short page  */
  }
  .condensed-header .Navigation-wrapper,
  .condensed-header .Navigation-button {
    height: 34px;
  }
  .condensed-header .Navigation-avatar {
    height: 24px;
    width: 24px;
    min-width: 24px;
  }

  .condensed-header .Navigation-displayName {
    font-size: 12px;
  }

  .condensed-header .Header__org-avatar {
    max-height: 48px;
  }

  .condensed-header .Navigation__link {
    font-size: 0.875rem;
  }

  .condensed-header .AltNavigation__trigger .icon .line {
    height: 3px;
    margin: 4px auto;
    width: 28px;
    transition: all 0.2s;
  }

  @keyframes fade-in {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  /* Navigation */
  .Navigation {
    align-items: center;
    display: none;
    justify-content: flex-end;
    white-space: nowrap;
  }
  .Navigation__link,
  .ChatButton {
    color: var(--color-tint2);
    margin-right: 1.5rem;
  }
  .Navigation__link[aria-current=true],
  .Navigation__link[aria-current=page] {
    color: var(--color-tint1);
    font-weight: 600;
  }
  .Navigation__more-button {
    background-color: transparent;
    border: 0;
    font-weight: 400;
    padding: 0;
  }
  .Navigation__more-button-toggle {
    font-size: 10px;
    padding-left: 6px;
  }

  /* AltNavigation */
  .AltNavigation {
    justify-content: center;
    align-items: center;
    display: flex;
    gap: 1.5rem;
  }
  .AltNavigation .NotificationBell {
    padding: 4px;
  }
  .AltNavigation .NotificationBell:after {
    right: 4px;
    top: 8px;
  }
  .AltNavigation__layout[data-reach-menu-list]{
    animation: slide-in 0.3s ease;
    background: var(--color-tint9);
    border: 0;
    border-left: 1px solid var(--color-tint5);
    bottom: 0;
    box-shadow: -2px 0 6px rgba(0,0,0,0.1);
    overflow: auto;
    padding: 0;
    position: fixed;
    right: 0;
    top: 0;
    width: 280px;
    z-index: 998;
  }
  .AltNavigation__layout [data-reach-menu-item] { outline: none }
  .AltNavigation__link {
    color: var(--color-tint1);
    display: block;
    border-bottom: 1px solid var(--color-tint6);
    padding: 1rem;
  }
  .AltNavigation__link[data-selected] {
    background: var(--color-tint7);
    color: var(--color-tint1);
  }
  .AltNavigation .Navigation__link,
  .AltNavigation .ChatButton {
    margin-right: 0;
  }
  .AltNavigation__trigger {
    background: transparent;
    border: 0;
    padding: 0;
  }
  .AltNavigation__person-box {
    border-bottom: 1px solid var(--color-tint5);
    display: flex;
    flex-direction: column;
    height: ${HEADER_HEIGHT}px;
    justify-content: center;
    padding: 0 1.5rem;
  }
  .AltNavigation__person-box.logged-in {
    justify-content: space-between;
    padding: 2rem 1.5rem 0 1.5rem;
  }
  .AltNavigation__person {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
  }
  .AltNavigation__person-avatar {
    border-radius: 100%;
    width: 32px;
    display: block;
    max-width: unset;
  }
  .AltNavigation__person-name,
  .AltNavigation__person-name[data-selected] {
    background: transparent;
    color: var(--color-tint1);
    font-size: 1rem;
    font-weight: 500;
    padding-left: 1rem;
  }
  .AltNavigation__person-actions {
    font-size: 0.875rem;
    padding-top: 0.5rem;
  }
  .AltNavigation__trigger .icon .line {
    background-color: var(--color-tint3);
    border-radius: 4px;
    display: block;
    height: 4px;
    margin: 6px auto;
    -webkit-transition: all 0.3s ease-in-out;
    -o-transition: all 0.3s ease-in-out;
    transition: all 0.3s ease-in-out;
    width: 32px;
  }

  .AltNavigation__trigger .icon .line:first-child {
    margin-top: 0;
  }

  .AltNavigation__trigger .icon .line:nth-child(3){ margin-bottom: 0 }
  .AltNavigation__trigger .icon:hover { cursor: pointer }
  .AltNavigation__trigger[aria-expanded=true] .icon .line:nth-child(2) {
    opacity: 0;
  }
  .AltNavigation__trigger[aria-expanded=true] .icon .line:nth-child(1) {
    -webkit-transform: translateY(10px) rotate(45deg);
    -ms-transform: translateY(10px) rotate(45deg);
    -o-transform: translateY(10px) rotate(45deg);
    transform: translateY(10px) rotate(45deg);
  }
  .AltNavigation__trigger[aria-expanded=true] .icon .line:nth-child(3) {
    -webkit-transform: translateY(-10px) rotate(-45deg);
    -ms-transform: translateY(-10px) rotate(-45deg);
    -o-transform: translateY(-10px) rotate(-45deg);
    transform: translateY(-10px) rotate(-45deg);
  }
  @keyframes slide-in {
    0% { transform: translateX(100%) }
    100% { transform: translateX(0) }
  }

  @media (min-width: ${CUTOFF_WIDTH}px) {
    .Navigation { display: flex }
    .AltNavigation { display: none }
  }

  /* LoggedInLinks */
  .LoggedInLinks {
    align-items: flex-start;
    display: flex;
    margin: 0 -1.5rem
  }
  .LoggedInLinks > * { flex: 1 }
  .LoggedInLinks > div:last-child { border-left: 1px solid var(--color-tint5) }
  .LoggedInLinks [data-reach-menu-item],
  .LoggedInLinks [data-selected] {
    background: var(--color-tint7);
    color: var(--color-tint1);
    display: block;
    flex: 1;
    font-size: 0.875rem;
    line-height: 1;
    padding: 0.75rem;
    text-align: center;
  }

  /* NotificationBell */
  .NotificationBell--has-notifications {
    position: relative;
  }

  .Navigation .nav-icon {
    width: 24px;
    height: 24px;
  }

  @media screen and (pointer: fine) {
    .Navigation .nav-icon {
      width: 20px;
      height: 20px;
    }
  }

  .condensed-header .Navigation .nav-icon {
    width: 20px;
    height: 20px;
  }

  .AltNavigation .nav-icon {
    width: 24px;
    height: 24px;
  }

  .NotificationBell--has-notifications:after {
    background-color: var(--color-ruby);
    border-radius: 100%;
    border: 1px solid var(--color-tint10);
    content: "";
    position: absolute;
    height: 7px;
    right: 0;
    top: 4px;
    width: 7px;
  }

  /* utility */
  .display-flex { display: flex }
  .align-items-center { align-items: center }
  .justify-content-space-between { justify-content: space-between }
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  /* Chat Components */
  .ChatButton {
    appearance: none;
    background: none;
    border: none;
    color: var(--color-tint2);
    cursor: pointer;
    padding: 0;
    position: relative;
  }

  .ChatButton.is-open {
    color: var(--color-tint1);
  }

  .ChatButton--unread:after {
    background-color: var(--color-ruby);
    border-radius: 100%;
    border: 1px solid var(--color-tint10);
    content: "";
    position: absolute;
    height: 7px;
    right: -3px;
    top: 2px;
    width: 7px;
  }
`
  return <style>{headerStyleString}</style>
}

Link.propTypes = {
  as: any,
}

function Link({ as: Component = "a", ...props }) {
  return <Component {...props} />
}

NotificationBell.propTypes = {
  notificationCount: number.isRequired,
  notificationLink: object.isRequired,
}

function NotificationBell({ notificationCount, notificationLink }) {
  return (
    <Link
      {...notificationLink}
      className={`Navigation__link NotificationBell ${
        notificationCount > 0 && "NotificationBell--has-notifications"
      }`}
    >
      <span>
        <NotificationIcon notificationCount={notificationCount} />
      </span>
    </Link>
  )
}

NotificationIcon.propTypes = {
  notificationCount: number.isRequired,
}

function NotificationIcon({ notificationCount }) {
  return (
    <Symbol
      symbol={`${groupsIcons}#notification`}
      className="p-r nav-icon"
      aria-label={`${notificationCount} notifications`}
    />
  )
}

// eslint-disable-next-line react/prop-types
function ChevronIcon({ style = {} }) {
  return (
    <svg className="symbol" role="img" title="down-chevron icon" style={style}>
      <symbol
        xmlns="http://www.w3.org/2000/svg"
        id="Navigation-svg-symbol-down-chevron"
        viewBox="0 0 16 16"
      >
        <title>down-chevron</title>
        <polygon points="8.003 14.068 0.005 6.07 1.782 4.293 8.003 10.514 14.223 4.293 16 6.07 8.003 14.068" />
      </symbol>
      <use href="#Navigation-svg-symbol-down-chevron" />
    </svg>
  )
}

MeAvatar.propTypes = {
  displayName: string,
  imageUrl: string.isRequired,
}

function MeAvatar({ displayName, imageUrl }) {
  const avatarStyleString = `
    .Navigation-avatar {
      border-radius: 100%;
      height: ${AVATAR_HEIGHT}px;
      width: ${AVATAR_WIDTH}px;
      min-width: ${AVATAR_WIDTH}px;
    }

    @media screen and (pointer:fine) {
      .Navigation-avatar {
        height: 24px;
        width: 24px;
        min-width: 24px;
      }
    }
  `

  return (
    <div>
      <style>{avatarStyleString}</style>
      <img
        className="Navigation-avatar d-b"
        width={AVATAR_WIDTH}
        height={AVATAR_HEIGHT}
        alt={`${displayName}'s avatar`}
        src={imageUrl}
      />
    </div>
  )
}

ChatButton.propTypes = {
  unread: bool.isRequired,
}

function ChatButton({ unread }) {
  const { toggleDrawer, isOpen, pcoChatEnabled } = useChatDrawer()

  if (!pcoChatEnabled) return null

  return (
    <button
      className={`ChatButton ${isOpen ? "is-open" : ""} ${
        unread ? "ChatButton--unread" : ""
      }`}
      onClick={toggleDrawer}
      aria-expanded={isOpen}
      aria-label={`${unread ? "New messages in chat" : "Chat"}`}
      aria-controls="chat-drawer"
      title={`${unread ? "New messages in chat" : "Chat"}`}
    >
      <ChatIcon />
      {unread && <span className="sr-only">New messages available</span>}
    </button>
  )
}

function ChatIcon() {
  return (
    <Symbol
      symbol={`${generalIcons}#text-message`}
      className="p-r nav-icon"
      aria-hidden="true"
    />
  )
}
